import React, { ReactNode } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import i18n from 'i18n-js';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';

import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { SimpleOnPress } from '@interfaces/base';

export enum SimpleButtonVariant {
  Fill = 'fill',
  Outline = 'outline',
  Dashed = 'dashed',
  Text = 'text',
  Error = 'error',
  Capsule = 'capsule',
}

export interface SimpleButtonProps extends Partial<SimpleOnPress> {
  C: ColorFunction;
  scale: ScaleFunction;
  style?: StyleProp<ViewStyle>;
  font?: typeof initialFont;
  text?: string;
  textStyle?: StyleProp<ViewStyle>;
  children?: ReactNode;
  variant?: SimpleButtonVariant;
  loading?: Boolean;
}

const SimpleButton = ({
  text,
  textStyle,
  children,
  onPress,
  C,
  style,
  scale = initialScale,
  font = initialFont,
  variant = SimpleButtonVariant.Fill,
  loading,
}: SimpleButtonProps) => {
  const variantsMap: Record<
    SimpleButtonVariant,
    { getContainerStyle: (pressHoverColor: (color: string) => string) => ViewStyle; textColor: string }
  > = {
    [SimpleButtonVariant.Capsule]: {
      getContainerStyle: (pressHoverColor) => {
        const colorKey = C.scheme === 'dark' ? 'Button/Dark/Secondary' : 'Button/Light/Secondary';

        return {
          backgroundColor: C(pressHoverColor(colorKey)),
          borderWidth: 0,
          justifyContent: 'center',
        };
      },
      textColor: C(C.scheme === 'dark' ? 'Button/Dark/Secondary Text' : 'Button/Light/Secondary Text'),
    },
    [SimpleButtonVariant.Fill]: {
      getContainerStyle: (pressHoverColor) => ({
        backgroundColor: C(pressHoverColor('Button/Light/Primary')),
        borderWidth: 0,
      }),
      textColor: C('Label/Light/Selected'),
    },
    [SimpleButtonVariant.Outline]: {
      getContainerStyle: () => ({
        backgroundColor: 'transparent',
        borderWidth: scale(1),
        borderColor: C('Label/Light/Tertiary'),
      }),
      textColor: C('Label/Light/Unselected'),
    },
    [SimpleButtonVariant.Dashed]: {
      getContainerStyle: () => ({
        backgroundColor: 'transparent',
        borderWidth: scale(1),
        borderColor: C('Label/Light/Tertiary'),
        borderStyle: 'dashed',
      }),
      textColor: C('Label/Light/Unselected'),
    },
    [SimpleButtonVariant.Error]: {
      getContainerStyle: () => ({
        backgroundColor: 'transparent',
        borderWidth: scale(1),
        borderColor: C('Label/Light/Error'),
      }),
      textColor: C('Label/Light/Error'),
    },
    [SimpleButtonVariant.Text]: {
      getContainerStyle: () => ({
        backgroundColor: 'transparent',
        borderWidth: 0,
      }),
      textColor: C('Label/Light/Tertiary'),
    },
  };

  return (
    <TouchableElement onPress={onPress}>
      {({ boxStyle, pressHoverColor }) => {
        const current = variantsMap[variant];

        return (
          <View
            style={[
              boxStyle,
              {
                alignItems: 'center',
                flexDirection: 'row',
                height: scale(28),
                paddingHorizontal: scale(13),
                borderRadius: scale(14.5),
              },
              current.getContainerStyle(pressHoverColor),
              style,
            ]}
          >
            {loading && (
              <View style={{ marginRight: scale(5) }}>
                <SmallLoading />
              </View>
            )}
            {text ? (
              <Text numberOfLines={1} ellipsizeMode="tail" style={[font['Button 2'], { color: current.textColor }, textStyle]}>
                {i18n.t(text)}
              </Text>
            ) : (
              children
            )}
          </View>
        );
      }}
    </TouchableElement>
  );
};

export default SimpleButton;
